<template>
  <CurrentEventDataProvider v-slot="{ logoUrl, features, data }">
    <ChatDataProvider>
      <UsersDataProvider>
        <!-- Sticky shell keeps the bar and the event name (below the
             separator line) pinned together. -->
        <div class="navbar-shell top-0 z-30 sticky">
          <div
            class="navbar flex justify-between items-center h-16 px-4 md:px-6 bg-white border-b border-grey"
          >
            <a href="#main-content" class="skip-nav-link">{{
              $t('Components.shared.Navbar.skipToContent')
            }}</a>

            <!-- LEFT: Brand (icon + event name + date) -->
            <div
              class="brand-wrap flex items-center cursor-pointer flex-shrink min-w-0"
              style="gap: 12px"
              @click="goToHomePage()"
            >
              <div
                class="brand-icon flex items-center justify-center rounded-lg h-14 flex-shrink-0"
              >
                <img
                  v-if="logoUrl"
                  :src="logoUrl"
                  :alt="$t('Components.shared.Navbar.eventLogo')"
                  class="brand-icon__img object-contain"
                />
                <div
                  v-else
                  class="h-10 w-10 flex items-center justify-center rounded-lg"
                  style="
                    background-color: transparent;
                    color: var(--color-text, #000000);
                  "
                >
                  <i class="fas fa-home text-md" aria-hidden="true"></i>
                </div>
              </div>
              <!-- Desktop: event name beside the logo (mobile shows it below the bar). -->
              <span
                v-if="data && data.eventName"
                class="brand-event-name__text brand-event-name__text--inline hidden md:block"
                >{{ data.eventName }}</span
              >
            </div>

            <!-- Mobile sidebar tabs (Stage/webapp only) -->
            <div
              v-if="
                tabs.length &&
                $route.name !== 'lobby' &&
                $route.name !== 'program' &&
                $route.name !== 'profile' &&
                $route.name !== 'live-stream' &&
                showNav &&
                mobileMenuOpen
              "
            >
              <SideBarTabs
                class="flex lg:hidden"
                :class="{ 'navbar-sidebar': mobileMenuOpen }"
                :tabs="tabs"
                :closeMobileMenu="closeMobileMenu"
              />
            </div>

            <!-- RIGHT: Desktop nav + bells + mobile hamburger -->
            <div class="flex items-center flex-shrink-0" style="gap: 14px">
              <!-- ═══ DESKTOP NAV (hidden on mobile ≤640px) ═══ -->
              <nav
                class="nav-desktop"
                :aria-label="$t('Components.shared.Navbar.mainNavigation')"
              >
                <router-link
                  v-if="isEventInfoVisible"
                  to="/"
                  class="nav-link"
                  :class="{ 'nav-link--active': isEventInfoActive }"
                >
                  <i class="far fa-home" aria-hidden="true"></i>
                  <span>{{ $t('Components.shared.Navbar.lobby') }}</span>
                </router-link>

                <router-link
                  v-if="isMainStageShown"
                  to="/program"
                  class="nav-link"
                  :class="{ 'nav-link--active': isProgramActive }"
                >
                  <i class="far fa-calendar" aria-hidden="true"></i>
                  <span>{{ mainStageCaption }}</span>
                </router-link>

                <router-link
                  v-if="isAiMatchingEnabled"
                  :to="{ name: 'dashboard' }"
                  class="nav-link"
                  :class="{ 'nav-link--active': isAiMatchingActive }"
                >
                  <i class="fas fa-sparkles nav-ai-icon" aria-hidden="true"></i>
                  <span>{{ $t('Components.shared.Navbar.networking') }}</span>
                </router-link>

                <div
                  class="profile-dropdown-wrap"
                  v-click-outside="closeProfileDropdown"
                >
                  <button
                    class="nav-link"
                    :class="{
                      'nav-link--active': isProfileActive || profileDropdownOpen
                    }"
                    @click="toggleProfileDropdown"
                    type="button"
                    aria-haspopup="menu"
                    :aria-expanded="profileDropdownOpen ? 'true' : 'false'"
                  >
                    <i class="far fa-user" aria-hidden="true"></i>
                    <span>{{ $t('Components.shared.Navbar.profile') }}</span>
                    <i
                      class="fas fa-chevron-down profile-dropdown-wrap__chevron"
                      :class="{
                        'profile-dropdown-wrap__chevron--open':
                          profileDropdownOpen
                      }"
                      aria-hidden="true"
                    ></i>
                  </button>

                  <transition name="notif-fade">
                    <div
                      v-if="profileDropdownOpen"
                      class="profile-dropdown"
                      role="menu"
                      @keydown.escape="closeProfileDropdown"
                    >
                      <router-link
                        to="/profile"
                        class="profile-dropdown__item"
                        @click.native="closeProfileDropdown"
                      >
                        <div
                          class="profile-dropdown__icon profile-dropdown__icon--teal"
                        >
                          <i class="far fa-user"></i>
                        </div>
                        <div class="profile-dropdown__label">
                          {{ $t('Components.shared.Navbar.myProfile') }}
                        </div>
                      </router-link>
                      <button
                        class="profile-dropdown__item"
                        @click="openWcagReportModal"
                        type="button"
                      >
                        <div
                          class="profile-dropdown__icon profile-dropdown__icon--green"
                        >
                          <i class="fas fa-universal-access"></i>
                        </div>
                        <div class="profile-dropdown__label">
                          {{ $t('Components.shared.Navbar.wcagReport') }}
                        </div>
                      </button>
                    </div>
                  </transition>
                </div>
              </nav>

              <!-- ═══ THEME TOGGLE (participant routes only) ═══ -->
              <button
                v-if="isParticipantRoute"
                type="button"
                class="theme-toggle-btn"
                :class="{ 'theme-toggle-btn--dark': isDarkTheme }"
                @click="toggleTheme"
                :aria-label="themeToggleLabel"
                :title="themeToggleLabel"
              >
                <i v-if="isDarkTheme" class="fas fa-sun" aria-hidden="true"></i>
                <i v-else class="fas fa-moon" aria-hidden="true"></i>
              </button>

              <!-- ═══ NOTIFICATION BELL (always visible) ═══ -->
              <div class="notif-wrap" v-click-outside="closeNotifications">
                <button
                  class="notif-btn"
                  :class="{ 'notif-btn--open': notifOpen }"
                  @click="toggleNotifications"
                  :aria-label="
                    notifUnreadCount
                      ? $t('Components.shared.Navbar.notificationsUnread', {
                          count: notifUnreadCount
                        })
                      : $t('Components.shared.Navbar.notifications')
                  "
                  :aria-expanded="notifOpen ? 'true' : 'false'"
                  aria-haspopup="dialog"
                  type="button"
                >
                  <i class="far fa-bell"></i>
                  <span v-if="notifUnreadCount" class="notif-badge">{{
                    notifUnreadCount > 9 ? '9+' : notifUnreadCount
                  }}</span>
                </button>

                <transition name="notif-fade">
                  <div
                    v-if="notifOpen"
                    class="notif-panel"
                    role="dialog"
                    :aria-label="$t('Components.shared.Navbar.notifications')"
                    @keydown.escape="closeNotifications"
                  >
                    <div class="notif-panel__header">
                      <h3 class="notif-panel__title">
                        {{
                          notifSettingsOpen
                            ? $t(
                                'Components.shared.Navbar.notificationSettings'
                              )
                            : $t('Components.shared.Navbar.notifications')
                        }}
                      </h3>
                      <div class="notif-panel__header-actions">
                        <button
                          v-if="!notifSettingsOpen && notifications.length"
                          class="notif-panel__action"
                          @click="markAllNotificationsRead"
                        >
                          {{ $t('Components.shared.Navbar.markAllRead') }}
                        </button>
                        <button
                          class="notif-panel__icon-action"
                          :class="{
                            'notif-panel__icon-action--active':
                              notifSettingsOpen
                          }"
                          :aria-label="
                            notifSettingsOpen
                              ? $t(
                                  'Components.shared.Navbar.backToNotifications'
                                )
                              : $t(
                                  'Components.shared.Navbar.notificationSettings'
                                )
                          "
                          @click="notifSettingsOpen = !notifSettingsOpen"
                          type="button"
                        >
                          <i
                            :class="
                              notifSettingsOpen
                                ? 'fas fa-arrow-left'
                                : 'fas fa-cog'
                            "
                          ></i>
                        </button>
                      </div>
                    </div>

                    <!-- ── Settings view — choose which categories to receive ── -->
                    <div v-if="notifSettingsOpen" class="notif-settings">
                      <p class="notif-settings__hint">
                        {{
                          $t(
                            'Components.shared.Navbar.notificationSettingsHint'
                          )
                        }}
                      </p>
                      <label
                        v-for="cat in visibleNotifCategories"
                        :key="cat.key"
                        class="notif-setting"
                      >
                        <div class="notif-setting__text">
                          <span class="notif-setting__label">{{
                            cat.label
                          }}</span>
                          <span class="notif-setting__desc">{{
                            cat.description
                          }}</span>
                        </div>
                        <span
                          class="notif-toggle"
                          :class="{
                            'notif-toggle--on':
                              notifPreferences[cat.key] !== false
                          }"
                        >
                          <input
                            type="checkbox"
                            class="notif-toggle__input"
                            :checked="notifPreferences[cat.key] !== false"
                            @change="
                              toggleNotifPreference(
                                cat.key,
                                $event.target.checked
                              )
                            "
                          />
                          <span class="notif-toggle__track"
                            ><span class="notif-toggle__thumb"></span
                          ></span>
                        </span>
                      </label>
                    </div>

                    <!-- ── List view ── -->
                    <template v-else>
                      <div
                        v-if="notifications.length"
                        class="notif-panel__list"
                      >
                        <button
                          v-for="n in notifications"
                          :key="n.id"
                          class="notif-item"
                          :class="{ 'notif-item--unread': !n.read }"
                          @click="onNotificationClick(n)"
                        >
                          <div
                            class="notif-item__icon"
                            :class="`notif-item__icon--${n.type}`"
                          >
                            <i :class="iconForType(n.type)"></i>
                          </div>
                          <div class="notif-item__body">
                            <div class="notif-item__title">{{ n.title }}</div>
                            <div v-if="n.message" class="notif-item__message">
                              {{ n.message }}
                            </div>
                            <div class="notif-item__time">
                              {{ formatNotifTime(n.timestamp) }}
                            </div>
                          </div>
                          <span v-if="!n.read" class="notif-item__dot"></span>
                        </button>
                      </div>

                      <div v-else class="notif-panel__empty">
                        <i class="far fa-bell-slash"></i>
                        <p>{{ $t('Components.shared.Navbar.allCaughtUp') }}</p>
                        <span>{{
                          $t('Components.shared.Navbar.notificationsEmptyHint')
                        }}</span>
                      </div>

                      <div
                        v-if="notifications.length"
                        class="notif-panel__footer"
                      >
                        <button
                          class="notif-panel__clear"
                          @click="clearAllNotifications"
                        >
                          {{ $t('Components.shared.Navbar.clearAll') }}
                        </button>
                      </div>
                    </template>
                  </div>
                </transition>
              </div>

              <!-- Support chat (desktop only — also in mobile menu) -->
              <button
                v-if="isSupportEnabled"
                v-show="chatWidgetLoaded"
                class="nav-support-btn"
                :aria-label="$t('Components.shared.Navbar.openSupport')"
                @click="toggleLiveChatWindow"
              >
                <i class="fal fa-headset text-dark" aria-hidden="true"></i>
              </button>

              <!-- ═══ MOBILE HAMBURGER (visible ≤640px only) ═══ -->
              <button
                class="mobile-hamburger"
                ref="mobileHamburgerBtn"
                :class="{ 'mobile-hamburger--open': mobileNavOpen }"
                @click.stop="mobileNavOpen = !mobileNavOpen"
                :aria-label="$t('Components.shared.Navbar.openMenu')"
                type="button"
              >
                <i :class="mobileNavOpen ? 'fas fa-times' : 'fas fa-bars'"></i>
              </button>
            </div>

            <!-- ═══ MOBILE MENU OVERLAY ═══
               Event Info / Program / My Profile have moved to the
               bottom-nav pill below; this overlay only carries the
               secondary actions (WCAG, Support). Event Badge + Vouchers
               now live in the Profile info tab on the profile page. -->
            <transition name="mobile-menu-slide">
              <div
                v-if="mobileNavOpen"
                class="mobile-menu"
                role="menu"
                :aria-label="$t('Components.shared.Navbar.mainNavigation')"
                @click.stop
                @keydown.escape="mobileNavOpen = false"
              >
                <!-- Feature options (Sponsors / Speakers / Information / …).
                   Shown from any route; tapping one opens the live-stream
                   view on that tab. Only rendered when at least one option
                   is enabled for the attendee's platform. -->
                <template v-if="liveStreamMenuItems.length">
                  <div class="mobile-menu__section-label">
                    {{ $t('Components.shared.Navbar.features') }}
                  </div>
                  <button
                    v-for="tab in liveStreamMenuItems"
                    :key="`ls-tab-${tab.key}`"
                    class="mobile-menu__item"
                    @click="selectLiveStreamTab(tab.key)"
                    type="button"
                    role="menuitem"
                  >
                    <div class="mobile-menu__icon mobile-menu__icon--teal">
                      <i :class="tab.icon"></i>
                    </div>
                    <div class="mobile-menu__label">{{ tab.label }}</div>
                  </button>
                  <div
                    class="mobile-menu__divider"
                    v-if="liveStreamMenuItems.length"
                  ></div>
                </template>

                <button
                  class="mobile-menu__item"
                  @click="
                    mobileNavOpen = false;
                    openWcagReportModal();
                  "
                  type="button"
                  role="menuitem"
                >
                  <div class="mobile-menu__icon mobile-menu__icon--green">
                    <i class="fas fa-universal-access"></i>
                  </div>
                  <div class="mobile-menu__label">
                    {{ $t('Components.shared.Navbar.wcagReport') }}
                  </div>
                </button>

                <button
                  v-if="isSupportEnabled && chatWidgetLoaded"
                  class="mobile-menu__item"
                  @click="
                    mobileNavOpen = false;
                    toggleLiveChatWindow();
                  "
                  type="button"
                  role="menuitem"
                >
                  <div class="mobile-menu__icon mobile-menu__icon--green">
                    <i class="fal fa-headset"></i>
                  </div>
                  <div class="mobile-menu__label">
                    {{ $t('Components.shared.Navbar.support') }}
                  </div>
                </button>
              </div>
            </transition>

            <!-- ═══ MOBILE BOTTOM-NAV PILL ═══
               Shown only at ≤640px. The primary destinations (Event Info,
               Program, My Profile) live here as a floating bottom-pill so
               attendees don't have to open the hamburger to reach them.
               Lives inside the navbar's root div to keep the slot single-
               rooted; `position: fixed` lets it escape into the viewport. -->
            <nav
              class="bottom-nav"
              role="navigation"
              :aria-label="$t('Components.shared.Navbar.primaryNavigation')"
            >
              <router-link
                v-if="isEventInfoVisible"
                to="/"
                class="bottom-nav__item"
                :class="{ 'bottom-nav__item--active': activeNavKey === 'info' }"
                :aria-label="$t('Components.shared.Navbar.lobby')"
                @click.native="onRouteNavClick"
              >
                <i class="far fa-home" aria-hidden="true"></i>
                <span class="bottom-nav__label">{{
                  $t('Components.shared.Navbar.lobby')
                }}</span>
              </router-link>
              <router-link
                v-if="isMainStageShown"
                to="/program"
                class="bottom-nav__item"
                :class="{
                  'bottom-nav__item--active': activeNavKey === 'program'
                }"
                :aria-label="mainStageCaption"
                @click.native="onProgramNavClick"
              >
                <i class="far fa-calendar" aria-hidden="true"></i>
                <span class="bottom-nav__label">{{ mainStageCaption }}</span>
              </router-link>
              <!-- Agenda — mobile bottom-nav only. Opens the Program page's
                 agenda panel in place (navigating there first if needed). -->
              <button
                v-if="isAgendaNavEnabled"
                type="button"
                class="bottom-nav__item"
                :class="{
                  'bottom-nav__item--active': activeNavKey === 'agenda'
                }"
                :aria-label="$t('Views.Program.agenda')"
                @click="openAgendaTab"
              >
                <i class="far fa-calendar-alt" aria-hidden="true"></i>
                <span class="bottom-nav__label">{{
                  $t('Views.Program.agenda')
                }}</span>
              </button>
              <!-- Timeline / Posts feed — kept as its own entry (distinct
                 "feed" glyph) so it is clearly separate from direct Chat. -->
              <button
                v-if="isPostsTabEnabled"
                type="button"
                class="bottom-nav__item"
                :class="{
                  'bottom-nav__item--active': activeNavKey === 'posts'
                }"
                :aria-label="$t('Components.shared.Navbar.posts')"
                @click="openPostsTab"
              >
                <i class="far fa-stream" aria-hidden="true"></i>
                <span class="bottom-nav__label">{{
                  $t('Components.shared.Navbar.posts')
                }}</span>
              </button>
              <!-- Direct messages / Chat — its own entry, distinct
                 "conversation" glyph, with an unread badge. Opens the
                 chat as an in-place overlay (below) instead of routing to
                 the full-page /chat, so the bottom bar stays visible and
                 this item can highlight while chat is open. -->
              <button
                v-if="isDirectMessageEnabled"
                type="button"
                class="bottom-nav__item"
                :class="{ 'bottom-nav__item--active': activeNavKey === 'chat' }"
                :aria-label="
                  unreadChatsCount
                    ? $t('Components.shared.Navbar.chatUnread', {
                        count: unreadChatsCount
                      })
                    : $t('Components.shared.Navbar.chat')
                "
                :aria-pressed="chatPanelOpen ? 'true' : 'false'"
                @click="toggleChatPanel"
              >
                <span class="bottom-nav__icon-wrap">
                  <i class="far fa-comments" aria-hidden="true"></i>
                  <span v-if="unreadChatsCount" class="bottom-nav__badge">{{
                    unreadChatsCount > 9 ? '9+' : unreadChatsCount
                  }}</span>
                </span>
                <span class="bottom-nav__label">{{
                  $t('Components.shared.Navbar.chat')
                }}</span>
              </button>
              <router-link
                v-if="isAiMatchingEnabled"
                :to="{ name: 'dashboard' }"
                class="bottom-nav__item"
                :class="{
                  'bottom-nav__item--active': activeNavKey === 'networking'
                }"
                :aria-label="$t('Components.shared.Navbar.networking')"
                @click.native="onRouteNavClick"
              >
                <i class="fas fa-sparkles nav-ai-icon" aria-hidden="true"></i>
                <span class="bottom-nav__label">{{
                  $t('Components.shared.Navbar.networking')
                }}</span>
              </router-link>
              <router-link
                to="/profile"
                class="bottom-nav__item"
                :class="{
                  'bottom-nav__item--active': activeNavKey === 'profile'
                }"
                :aria-label="$t('Components.shared.Navbar.myProfile')"
                @click.native="onRouteNavClick"
              >
                <i class="far fa-user" aria-hidden="true"></i>
                <span class="bottom-nav__label">{{
                  $t('Components.shared.Navbar.profile')
                }}</span>
              </router-link>
            </nav>

            <!-- ═══ MOBILE CHAT PANEL ═══
               Direct-messages surface opened from the bottom-nav Chat
               item. Rendered here (inside the always-mounted Navbar) as a
               fixed overlay that sits BELOW the bottom-nav pill, so the
               bar stays visible and the Chat item stays highlighted while
               chat is open. Mobile only. -->
            <transition name="mobile-menu-slide">
              <div
                v-if="chatPanelOpen"
                class="mobile-chat-panel"
                role="dialog"
                :aria-label="$t('Components.shared.Navbar.chat')"
                @keydown.escape="closeChatPanel"
              >
                <PrivateChatModal :recipient="null" @close="closeChatPanel" />
              </div>
            </transition>
          </div>

          <!-- Event name, below the navbar separator line. -->
          <div
            v-if="isProgramActive && !agendaOpen && data && data.eventName"
            class="brand-event-name px-4 md:hidden cursor-pointer"
            @click="goToHomePage()"
          >
            <span class="brand-event-name__text">{{ data.eventName }}</span>
          </div>
        </div>
      </UsersDataProvider>
    </ChatDataProvider>
  </CurrentEventDataProvider>
</template>

<script>
import { getApplicablePlatforms } from '@/utils';
import { resolveParticipationType } from '@/helpers/redirection-helpers';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import { togglesSupportChat } from '@/mixins';
import UserProfile from '@/views/Stage/UserProfile';
import TicketModal from '@/components/shared/TicketModal';
import SideBarTabs from '@/components/shared/SideBarTabs';
import LobbyParticipants from '@/views/Stage/Participant/LobbyParticipants';
import ChatLobby from '@/components/chat/lobby/ChatLobby';
import WcagReportModal from '@/components/WcagReportModal';
import Conversations from '@/views/Stage/Conversations';
import ChatDataProvider from '@/components/providers/ChatDataProvider';
import PrivateChatModal from '@/components/PrivateChatModal';
import { intlLocale } from '@/i18n';

// Lightweight click-outside directive scoped to this component so the
// notifications panel closes when the user clicks elsewhere.
const clickOutsideDirective = {
  bind(el, binding) {
    el.__clickOutsideHandler__ = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        if (typeof binding.value === 'function') binding.value(event);
      }
    };
    document.addEventListener('click', el.__clickOutsideHandler__);
  },
  unbind(el) {
    document.removeEventListener('click', el.__clickOutsideHandler__);
    delete el.__clickOutsideHandler__;
  }
};

export default {
  name: 'Navbar',
  components: { ChatDataProvider, SideBarTabs, PrivateChatModal },
  directives: { 'click-outside': clickOutsideDirective },
  mixins: [togglesSupportChat],
  props: {
    showNav: {
      default: false
    },
    showMobileMenu: {
      default: true
    },
    fullWidth: {
      default: true
    },
    tabs: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      lobbyElements: [],
      listOpen: false,
      mobileMenuOpen: false,
      notifOpen: false,
      profileDropdownOpen: false,
      mobileNavOpen: false,
      sessionTickInterval: null,
      announcedSessions: {
        started: new Set(),
        ended: new Set(),
        reminded: new Set()
      },
      // sessionId → last-seen { start, end } epoch ms, used to detect
      // admin schedule changes (time moved) and notify the participant.
      sessionSchedule: {},
      // Toggle between the notification list and the settings view in
      // the bell panel.
      notifSettingsOpen: false,
      // Track message counts per chat and timeline length so we detect
      // new messages via polling (avoids Vue 2 deep-watcher pitfalls).
      seenChatMessages: {},
      seenTimelineCount: 0,
      seenPromotedIds: new Set(),
      bootstrapped: false,
      notifPollTimer: null,
      // Bumping this counter re-runs `isPhysicalAttendee` which
      // reads localStorage (non-reactive by default). Gets bumped
      // on `storage` events (cross-tab/profile changes) and on
      // route navigation so changes propagate immediately.
      participationTick: 0,
      // Bottom-nav "Chat" opens this in-place overlay (mobile) rather than
      // navigating to the full-page /chat route, so the bottom bar stays.
      chatPanelOpen: false,
      // Optimistic highlight for the bottom-nav "Posts" item. Posts opens
      // the stage's Posts tab, which on the /program route doesn't change
      // the URL — so we can't rely on the route alone to know it's active.
      postsActive: false,
      // True while the Program page's agenda panel is open (reported by
      // Program.vue via the `program:agenda-state` bus event).
      agendaOpen: false,
      chatTab: {
        enabled: () => true,
        key: 'conversations',
        icon: 'fa-comments',
        label: () => this.getEvent.captions.profileCaption,
        component: Conversations,
        isModal: false,
        onClick: this.openChat
      }
    };
  },

  computed: {
    ...mapGetters('CurrentEvent', [
      'getLocale',
      'getEvent',
      'isWebappEnabled',
      'isLobbyEnabled',
      'isStageEnabled',
      'features'
    ]),
    ...mapGetters('Authentication', {
      profile: 'getUser'
    }),
    // Physical attendees never see the event-info / lobby surface.
    // Resolved by the shared rule (event setup first, then the stored
    // choice on hybrid events); the `participationTick` counter forces
    // re-evaluation on storage changes from another tab.
    isPhysicalAttendee() {
      // eslint-disable-next-line no-unused-vars
      const _ = this.participationTick; // touch to force reactivity
      return resolveParticipationType() === 'physical';
    },
    isEventInfoVisible() {
      // Event Info === the lobby surface. Shown only when the admin's
      // "Lobby" toggle is ON *and* the attendee is not attending
      // physically — a physical attendee never sees the lobby, even
      // when the admin has enabled it.
      return this.isLobbyEnabled && !this.isPhysicalAttendee;
    },
    ...mapGetters('Authentication', ['getDefaultImage']),
    ...mapGetters('Chat', [
      'getChats',
      'getUnreadChatMessages',
      'getUnreadChatsCount'
    ]),
    ...mapGetters('Notifications', {
      notifications: 'getItems',
      notifUnreadCount: 'unreadCount',
      notifCategories: 'categories',
      notifPreferences: 'getPreferences'
    }),
    ...mapGetters('Theme', {
      themeMode: 'getMode',
      isDarkTheme: 'isDark',
      isParticipantRoute: 'isParticipantRoute'
    }),
    ...mapGetters('Agendas', { agendaSessions: 'getSessions' }),
    chatList() {
      return this.$store.state.Chat?.chats || [];
    },
    timelineFeed() {
      return this.$store.state.Timeline?.timelineItems || [];
    },
    promotedTimelineItems() {
      return this.$store.state.Timeline?.promotedTimelineItems || [];
    },
    ownUserId() {
      return this.profile && this.profile.UserID;
    },
    isSupportEnabled() {
      return getApplicablePlatforms(this.$route).includes('webApp')
        ? this.features.webAppSupport
        : this.features.stageSupport;
    },
    // Single source of truth for which bottom-nav item is highlighted, so
    // exactly ONE lights up at a time. Chat (an in-place overlay) and
    // Posts (a stage tab) open ON TOP of the current route, so they must
    // take precedence over — and suppress — the underlying route's item.
    // Otherwise both the route item (e.g. Program) and the overlay item
    // (Chat) showed as active at once.
    activeNavKey() {
      if (this.chatPanelOpen) return 'chat';
      if (this.postsActive) return 'posts';
      if (this.agendaOpen && this.$route.name === 'program') return 'agenda';
      const name = this.$route.name;
      if (name === 'private-chat') return 'chat';
      if (
        ['live-stream', 'program'].includes(name) &&
        this.$route.query.tab === 'chat'
      )
        return 'posts';
      if (this.isEventInfoActive) return 'info';
      if (this.isProgramActive) return 'program';
      if (this.isAiMatchingActive) return 'networking';
      if (this.isProfileActive) return 'profile';
      return null;
    },
    // Whether 1-on-1 (direct message) chat is enabled for this event. Uses the
    // same robust detection as LiveStream.isChatFeatureEnabled: default to
    // enabled, only treat as OFF when every known chat flag is present AND
    // falsy (so events whose flag names differ aren't wrongly hidden).
    isDirectMessageEnabled() {
      const f = this.features || {};
      const keys = [
        'lobbyChatEnabled',
        'stageChatEnabled',
        'webAppChat',
        'lobbyChat',
        'oneToOneChat'
      ];
      const present = keys.filter((k) => k in f);
      if (!present.length) return true;
      return present.some((k) => !!f[k]);
    },
    // Notification categories to show in settings. The "Direct messages"
    // category is only relevant when 1-on-1 chat is enabled for the event.
    visibleNotifCategories() {
      const chatOn = this.isDirectMessageEnabled;
      return (this.notifCategories || []).filter(
        (cat) => cat.key !== 'messages' || chatOn
      );
    },
    // Live-stream side-panel tabs surfaced inside the navbar's mobile
    // menu when the user is on the live-stream route. Mirrors the
    // feature gating in LiveStream.vue's own menuItems computed so a
    // tab disabled by the admin doesn't appear here either.
    liveStreamIsOnWebapp() {
      // Same rule as LiveStream.vue's isOnWebapp, so the mobile menu
      // evaluates the same webAppNavigation* / stageTab* flags as the
      // page it opens.
      // eslint-disable-next-line no-unused-vars
      const _ = this.participationTick; // touch to force reactivity
      return getApplicablePlatforms(this.$route).includes('webApp');
    },
    // Same rule as LiveStream.isPostEnabled so the mobile menu, the bottom
    // bar and the desktop side menu always agree.
    isPostsTabEnabled() {
      const f = this.features || {};
      const onWebapp = this.liveStreamIsOnWebapp;
      const timelineOn = onWebapp ? !!f.webAppTimeline : !!f.stageTabTimeline;
      const posting = onWebapp
        ? f.webAppTimelinePosting !== false
        : f.stageFuncTimelinePosting !== false;
      const commenting = onWebapp
        ? f.webAppTimelineCommenting !== false
        : f.stageFuncTimelineCommenting !== false;
      return timelineOn && (posting || commenting);
    },
    liveStreamMenuItems() {
      // Feature options (Sponsors / Speakers / Information / Documents / …)
      // surfaced in the mobile hamburger from ANY route. Tapping one opens
      // the live-stream view on that tab (see selectLiveStreamTab).
      const f = this.features || {};
      const onWebapp = this.liveStreamIsOnWebapp;
      const flag = (webappKey, stageKey) =>
        onWebapp ? !!f[webappKey] : !!f[stageKey];
      const items = [
        {
          key: 'eventInfo',
          label: this.$t('Components.shared.Navbar.information'),
          icon: 'far fa-info-circle',
          on: flag('webAppNavigationInfoPages', 'stageTabInfoPages')
        },
        {
          key: 'docs',
          label: this.$t('Components.shared.Navbar.documents'),
          icon: 'far fa-file-alt',
          on: flag('webAppNavigationDocuments', 'stageTabDocuments')
        },
        {
          key: 'sponsors',
          label: this.$t('Components.shared.Navbar.sponsors'),
          icon: 'fas fa-star',
          on: flag('webAppNavigationSponsors', 'stageTabSponsors')
        },
        {
          key: 'speakers',
          label: this.$t('Components.shared.Navbar.speakers'),
          icon: 'fas fa-microphone',
          on: flag('webAppNavigationSpeakers', 'stageTabSpeakers')
        },
        {
          key: 'people',
          label: this.$t('Components.shared.Navbar.participants'),
          icon: 'far fa-user',
          on: flag('webAppNavigationParticipants', 'stageTabParticipants')
        },
        {
          key: 'interaction',
          label:
            (this.getEvent &&
              this.getEvent.captions &&
              this.getEvent.captions.gamificationCaption) ||
            this.$t('Components.shared.Navbar.interaction'),
          icon: 'far fa-window-restore',
          on: !!(
            f.stageTabCustom &&
            this.getEvent &&
            this.getEvent.gamificationUrl
          )
        }
      ];
      return items.filter((i) => i.on);
    },
    unreadChatsCount() {
      return this.getUnreadChatsCount;
    },
    themeToggleLabel() {
      return this.isDarkTheme
        ? this.$t('Components.shared.Navbar.switchToLightMode')
        : this.$t('Components.shared.Navbar.switchToDarkMode');
    },
    mainStageCaption() {
      const event = this.getEvent;

      const defaultCaption = this.$t('Components.shared.Navbar.mainstage');

      if (!event.lobbyElements) {
        return defaultCaption;
      }

      const mainStageCaption = event.lobbyElements.find(
        (x) => x.id === 'header'
      )?.options?.mainStageLabel;

      return mainStageCaption || defaultCaption;
    },
    informationCaption() {
      const event = this.getEvent;

      const defaultCaption = event.captions.informationCaption;

      if (!event.lobbyElements) {
        return defaultCaption;
      }

      const mainStageCaption = event.lobbyElements.find(
        (x) => x.id === 'information'
      )?.options?.label;

      return mainStageCaption || defaultCaption;
    },
    speakersCaption() {
      const event = this.getEvent;

      const defaultCaption = event.captions.speakersCaption;

      if (!event.lobbyElements) {
        return defaultCaption;
      }

      const mainStageCaption = event.lobbyElements.find(
        (x) => x.id === 'speakers'
      )?.options?.label;

      return mainStageCaption || defaultCaption;
    },
    agendaCaption() {
      const event = this.getEvent;

      const defaultCaption = event.captions.programCaption;

      if (!event.lobbyElements) {
        return defaultCaption;
      }

      const mainStageCaption = event.lobbyElements.find(
        (x) => x.id === 'agenda'
      )?.options?.label;

      return mainStageCaption || defaultCaption;
    },
    participantsCaption() {
      const event = this.getEvent;

      const defaultCaption = this.$t(
        'Components.shared.Navbar.participantsList'
      );

      if (!event.lobbyElements) {
        return defaultCaption;
      }

      const participantsCaption = event.lobbyElements.find(
        (x) => x.id === 'header'
      )?.options?.participantsLabel;

      return participantsCaption || defaultCaption;
    },
    sponsorCaption() {
      const event = this.getEvent;

      const defaultCaption = event.captions.sponsorsCaption;

      if (!event.lobbyElements) {
        return defaultCaption;
      }

      const mainStageCaption = event.lobbyElements.find(
        (x) => x.id === 'sponsors'
      )?.options?.label;

      return mainStageCaption || defaultCaption;
    },
    isMainStageShown() {
      // A stage the event has not enabled renders an empty page, so never link to it. Without
      // this an event with an empty lobby fell through to "return true" below and showed a
      // Main stage link that led nowhere.
      if (!this.isStageEnabled) {
        return false;
      }

      const event = this.getEvent;

      if (!event.lobbyElements) {
        return true;
      }

      return event.lobbyElements.find((x) => x.id === 'header')?.options
        ?.mainStageEnabled;
    },
    isInformationShown() {
      const event = this.getEvent;

      if (!event.lobbyElements) {
        return true;
      }

      return event.lobbyElements.some((x) => x.id === 'information');
    },
    isSpeakersShown() {
      const event = this.getEvent;

      if (!event.lobbyElements) {
        return event.features.lobbySpeakers;
      }
      return event.lobbyElements.some((x) => x.id === 'speakers');
    },
    isParticipantsShown() {
      const event = this.getEvent;

      if (!event.lobbyElements) {
        return event.features.stageTabParticipants;
      }

      return event.lobbyElements.find((x) => x.id === 'header')?.options
        ?.participantsEnabled;
    },
    isAgendaShown() {
      const event = this.getEvent;

      if (!event.lobbyElements) {
        return event.features.lobbyAgenda;
      }

      return event.lobbyElements.some((x) => x.id === 'agenda');
    },
    isMultipleSponsorsShown() {
      const event = this.getEvent;
      if (!event.lobbyElements) {
        return event.features.multipleSponsorsEnabled;
      }

      return event.lobbyElements.some((x) => x.id.startsWith('sponsors'));
    },
    isEventInfoActive() {
      return this.$route.name === 'lobby';
    },
    isProgramActive() {
      return this.$route.name === 'program';
    },
    isProfileActive() {
      // The matching-profile (Settings) page is reached from My Profile, so
      // highlight the Profile tab there rather than Networking.
      return ['profile', 'matching-profile'].includes(this.$route.name);
    },
    isAgendaNavEnabled() {
      const f = this.features || {};
      return this.liveStreamIsOnWebapp
        ? !!f.webAppNavigationAgenda
        : !!f.stageTabAgenda;
    },
    isAiMatchingEnabled() {
      return !!this.features?.aiMatchingEnabled;
    },
    isAiMatchingActive() {
      return [
        'dashboard',
        'ai-matches',
        'ai-match-sponsor',
        'my-meetings',
        'open-sessions'
      ].includes(this.$route.name);
    }
  },
  methods: {
    ...mapActions('Authentication', ['doLogout']),
    ...mapActions('CurrentEvent', ['changeLanguage']),
    ...mapActions('Notifications', {
      addNotification: 'add',
      markAllNotificationsReadAction: 'markAllRead',
      markNotificationRead: 'markRead',
      clearNotifications: 'clear',
      setNotifPreference: 'setPreference'
    }),
    toggleNotifPreference(category, enabled) {
      this.setNotifPreference({ category, enabled });
    },
    ...mapActions('Theme', { setThemeMode: 'setMode' }),
    toggleTheme() {
      this.setThemeMode(this.isDarkTheme ? 'light' : 'dark');
    },
    ...mapMutations('Stage', ['setCurrentTab']),
    ...mapMutations('Chat', ['setCurrentChatID', 'setIsChatList']),

    // ── Profile dropdown ───────────────────────────────────────────
    toggleProfileDropdown() {
      this.profileDropdownOpen = !this.profileDropdownOpen;
      if (this.profileDropdownOpen) this.notifOpen = false;
    },
    closeProfileDropdown() {
      this.profileDropdownOpen = false;
    },
    closeMobileNav(event) {
      // Don't close if the click was on the hamburger button itself
      // (that toggle is handled by its own @click.stop handler)
      if (
        this.$refs.mobileHamburgerBtn &&
        this.$refs.mobileHamburgerBtn.contains(event && event.target)
      ) {
        return;
      }
      this.mobileNavOpen = false;
    },
    onDocumentClickForMobileMenu(event) {
      if (!this.mobileNavOpen) return;
      // If click is outside the menu AND outside the hamburger button, close
      const menu = this.$el.querySelector('.mobile-menu');
      const btn = this.$refs.mobileHamburgerBtn;
      if (menu && menu.contains(event.target)) return;
      if (btn && btn.contains(event.target)) return;
      this.mobileNavOpen = false;
    },
    // Surfaces a live-stream side-panel tab. Closes the mobile menu
    // first, then bubbles a $root event the LiveStream view listens
    // for — keeps the two components decoupled (no Vuex coupling for
    // a UI-only concern).
    selectLiveStreamTab(key) {
      this.mobileNavOpen = false;
      // On live-stream/program the view is already mounted and listening,
      // so just signal it. From any other route, navigate there and pass
      // the target tab as a query param the view reads on mount.
      if (
        this.$route.name === 'live-stream' ||
        this.$route.name === 'program'
      ) {
        this.$root.$emit('live-stream:set-tab', key);
      } else {
        // Always open the tab inside the Program page — the same event-wide
        // Posts the agenda page shows. The live-stream route would reuse the
        // last-watched session (a stale session's posts).
        this.$router
          .push({ name: 'program', query: { tab: key } })
          .catch(() => {});
      }
    },
    // Bottom-nav "Agenda": open the Program page's agenda panel. Already on
    // /program → signal it; otherwise navigate there with ?agenda=1.
    openAgendaTab() {
      if (this.chatPanelOpen) this.closeChatPanel();
      this.postsActive = false;
      this.mobileNavOpen = false;
      if (this.$route.name === 'program') {
        // Leave any open section (Posts, ...) first, so it isn't left open
        // underneath the agenda page.
        this.$root.$emit('live-stream:set-tab', null);
        // Highlight the Agenda item straight away (Program confirms it).
        this.agendaOpen = true;
        this.$root.$emit('program:open-agenda');
      } else {
        this.$router
          .push({ name: 'program', query: { agenda: '1' } })
          .catch(() => {});
      }
    },
    toggleChatPanel() {
      if (this.chatPanelOpen) {
        this.closeChatPanel();
      } else {
        this.mobileNavOpen = false;
        // Chat takes over as the sole active item.
        this.postsActive = false;
        this.chatPanelOpen = true;
        try {
          document.body.classList.add('overflow-hidden');
        } catch (e) {
          /* SSR / no document */
        }
      }
    },
    // Bottom-nav "Posts": open the stage's Posts tab and highlight this
    // item. On /program this doesn't change the route, so we flag it
    // locally; the $route watcher reconciles the flag on any navigation.
    openPostsTab() {
      if (this.chatPanelOpen) this.closeChatPanel();
      this.postsActive = true;
      this.selectLiveStreamTab('chat');
    },
    // Tapping a route-based bottom-nav item (Info / Program / Networking /
    // Profile) clears the Posts/Chat overlays so the route item is the one
    // that highlights — covers the case where the tapped route is already
    // current (no navigation fires) and the flags would otherwise linger.
    // Bottom-nav "Main stage": always clickable. When already on /program
    // (e.g. the Agenda item is the active one) the router ignores the
    // repeat navigation, so handle it here: make Main stage the active
    // item again and scroll to the top of the page.
    onProgramNavClick() {
      this.onRouteNavClick();
      // Always drop the open side-panel section (e.g. Posts) so Main stage
      // really shows the main stage — the route stays on /program, so the
      // section would otherwise stay open under a highlighted Main stage.
      this.$root.$emit('live-stream:set-tab', null);
      if (this.$route.name === 'program') {
        this.$root.$emit('program:close-agenda');
        this.agendaOpen = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    onRouteNavClick() {
      try {
        sessionStorage.removeItem('tappin_program_return');
      } catch (e) {
        /* ignore */
      }
      this.postsActive = false;
      if (this.chatPanelOpen) this.closeChatPanel();
    },
    closeChatPanel() {
      this.chatPanelOpen = false;
      try {
        document.body.classList.remove('overflow-hidden');
      } catch (e) {
        /* SSR / no document */
      }
    },
    openWcagReportModal() {
      this.profileDropdownOpen = false;
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        WcagReportModal,
        {},
        {
          adaptive: true,
          clickToClose: true,
          width: '92%',
          height: 'auto',
          minWidth: 320,
          maxWidth: 560
        },
        { closed: () => document.body.classList.remove('overflow-hidden') }
      );
    },

    // ── Notifications panel ─────────────────────────────────────────
    toggleNotifications() {
      this.notifOpen = !this.notifOpen;
      if (this.notifOpen) {
        this.profileDropdownOpen = false;
        // Mark all read shortly after opening so the badge clears
        // but the user still sees the unread state for a moment
        setTimeout(() => {
          if (this.notifOpen) this.markAllNotificationsReadAction();
        }, 1500);
      }
    },
    closeNotifications() {
      this.notifOpen = false;
    },
    markAllNotificationsRead() {
      this.markAllNotificationsReadAction();
    },
    clearAllNotifications() {
      this.clearNotifications();
      this.notifOpen = false;
    },
    onNotificationClick(n) {
      this.markNotificationRead(n.id);
      this.notifOpen = false;
      // Chat notifications open the actual conversation in the chat modal
      // (or navigate to it if a route is set).
      if (n.type === 'chat' && n.meta && n.meta.chatId) {
        this.openChatById(n.meta.chatId);
        return;
      }
      if (n.route) {
        this.$router.push(n.route).catch(() => {});
      }
    },
    openChatById(chatId) {
      if (!chatId) return;
      // Find the chat in the store and open the themed PrivateChatModal
      // scoped to the matching participant so the new message is visible
      // immediately in our design language.
      const chats = this.$store.state.Chat?.chats || [];
      const chat = chats.find((c) => c.ChatID === chatId);
      if (!chat) return;
      // Activate the chat in the Chat store so any open chat UI updates
      this.$store.dispatch('Chat/activateChat', chat).catch(() => {});
      // Pick the first participant who isn't us — that's the recipient
      const otherUser =
        (chat.Users || []).find((u) => u && u.UserID !== this.ownUserId) ||
        (chat.Users || [])[0];
      if (otherUser) {
        this.showPrivateChatModal(otherUser);
      }
    },
    showPrivateChatModal(recipient) {
      // Mobile: navigate to the dedicated /chat page so users get a
      // real screen with a back button instead of a popup. Desktop
      // keeps the modal experience.
      const isMobile =
        typeof window !== 'undefined' && window.innerWidth <= 768;
      if (isMobile) {
        const userId = recipient && recipient.UserID;
        this.$router
          .push({
            name: 'private-chat',
            params: userId ? { userId: String(userId) } : {}
          })
          .catch(() => {
            /* duplicate-nav errors are harmless */
          });
        return;
      }
      // Desktop: dock a LinkedIn-style chat window at the bottom-right.
      this.$store.dispatch('Chat/openChatWindow', recipient);
    },
    iconForType(type) {
      const map = {
        chat: 'far fa-comment-dots',
        'session-start': 'far fa-play-circle',
        'session-end': 'far fa-stop-circle',
        'session-reminder': 'far fa-clock',
        'schedule-change': 'far fa-calendar-alt',
        poll: 'fas fa-poll',
        info: 'far fa-bell',
        promoted: 'far fa-bullhorn',
        sponsor: 'far fa-building'
      };
      return map[type] || 'far fa-bell';
    },
    formatNotifTime(timestamp) {
      if (!timestamp) return '';
      const diff = Date.now() - timestamp;
      const sec = Math.round(diff / 1000);
      if (sec < 60) return this.$t('Components.shared.Navbar.justNow');
      const min = Math.round(sec / 60);
      if (min < 60) {
        return this.$t('Components.shared.Navbar.minutesAgo', { n: min });
      }
      const hr = Math.round(min / 60);
      if (hr < 24) {
        return this.$t('Components.shared.Navbar.hoursAgo', { n: hr });
      }
      const day = Math.round(hr / 24);
      return this.$t('Components.shared.Navbar.daysAgo', { n: day });
    },

    // ── Notification sources ────────────────────────────────────────
    // Instead of relying on deep watchers (which can miss nested mutations
    // in Vue 2), we poll the Vuex state every few seconds and compare
    // against our snapshot. This is simple and always works.
    startNotificationPoller() {
      if (this.notifPollTimer) return;
      // Kick off an immediate refresh so the baseline is snapshotted
      // against real data, then poll every 5 seconds. Errors in any
      // individual cycle must never stop the timer.
      const run = () => {
        this.pollForNotifications().catch((e) => {
          // eslint-disable-next-line no-console
          console.warn('[Notifications] poll cycle failed', e);
        });
      };
      // First run after 3s so initial data providers have time to load
      setTimeout(run, 3000);
      this.notifPollTimer = setInterval(run, 5000);
    },
    stopNotificationPoller() {
      if (this.notifPollTimer) {
        clearInterval(this.notifPollTimer);
        this.notifPollTimer = null;
      }
    },
    snapshotBaselines() {
      // Private chats — record total message count per chat
      const chats = this.$store.state.Chat?.chats || [];
      chats.forEach((chat) => {
        if (!chat || !chat.ChatID) return;
        this.seenChatMessages[chat.ChatID] = (chat.Messages || []).length;
      });
      // Live stream timeline — record count
      this.seenTimelineCount = (
        this.$store.state.Timeline?.timelineItems || []
      ).length;
      // Promoted items — record ids
      (this.$store.state.Timeline?.promotedTimelineItems || []).forEach(
        (item) => {
          if (item && item.TimelineID)
            this.seenPromotedIds.add(item.TimelineID);
        }
      );
      this.bootstrapped = true;
    },
    async refreshStoreData() {
      // Actively re-fetch Chat and Timeline data from the API on every
      // poll cycle so the Vuex stores are fresh. Without this, stores
      // only update via pusher — which can be a fake no-op stub when the
      // event details weren't loaded before the provider bound.
      try {
        await this.$store.dispatch('Chat/loadChatData');
      } catch (e) {
        /* ignore */
      }
      try {
        this.$store.commit('Timeline/setPromise', null);
        await this.$store.dispatch('Timeline/loadTimelineData');
      } catch (e) {
        /* ignore */
      }
    },
    async pollForNotifications() {
      // The store subscription above handles all the diff + notify
      // logic. This poller just pokes the API so that if pusher is
      // dead the store still sees fresh data on a timer.
      await this.refreshStoreData();
    },
    checkPrivateChats() {
      const chats = this.$store.state.Chat?.chats || [];
      chats.forEach((chat) => {
        if (!chat || !chat.ChatID || !Array.isArray(chat.Messages)) return;
        const prevCount = this.seenChatMessages[chat.ChatID] || 0;
        const currentCount = chat.Messages.length;
        if (currentCount <= prevCount) return;
        // New messages arrived — check the latest ones
        const newMsgs = chat.Messages.slice(prevCount);
        const incoming = newMsgs.filter(
          (m) => m && m.UserID !== this.ownUserId
        );
        if (incoming.length) {
          const last = incoming[incoming.length - 1];
          const sender = (chat.Users || []).find(
            (u) => u && u.UserID === last.UserID
          );
          const senderName = sender
            ? `${sender.Firstname || sender.firstName || ''} ${
                sender.Lastname || sender.lastName || ''
              }`.trim() || this.$t('Components.shared.Navbar.someone')
            : this.$t('Components.shared.Navbar.someone');
          const preview = (
            last.Message ||
            last.message ||
            this.$t('Components.shared.Navbar.sentYouAMessage')
          )
            .toString()
            .trim();
          this.addNotification({
            type: 'chat',
            title: this.$t('Components.shared.Navbar.sentYouAMessageTitle', {
              name: senderName
            }),
            message:
              preview.length > 120 ? preview.slice(0, 117) + '…' : preview,
            dedupeKey: `chat-${chat.ChatID}`,
            meta: { chatId: chat.ChatID, userId: last.UserID }
          });
        }
        this.seenChatMessages[chat.ChatID] = currentCount;
      });
    },
    checkTimelineChat() {
      const items = this.$store.state.Timeline?.timelineItems || [];
      const currentCount = items.length;
      const prevCount = this.seenTimelineCount || 0;
      if (currentCount <= prevCount) {
        this.seenTimelineCount = currentCount;
        return;
      }
      // New timeline items — check for user chat messages (not our own)
      const newItems = items.slice(0, currentCount - prevCount);
      const incoming = newItems.filter((i) => {
        if (!i || !i.Message) return false;
        const uid =
          i.UserID ||
          (i.Author && i.Author.UserID) ||
          (i.User && i.User.UserID);
        return uid && uid !== this.ownUserId;
      });
      if (incoming.length) {
        const last = incoming[0];
        const uid =
          last.UserID ||
          (last.Author && last.Author.UserID) ||
          (last.User && last.User.UserID);
        const name =
          (last.Author && last.Author.Firstname) ||
          (last.User && last.User.Firstname) ||
          this.$t('Components.shared.Navbar.someone');
        const tmp = document.createElement('div');
        tmp.innerHTML = (last.Message || '').replace(
          /<!--tappin-session:[^>]*-->/g,
          ''
        );
        const text = (tmp.textContent || tmp.innerText || '').trim();
        this.addNotification({
          type: 'chat',
          title: this.$t('Components.shared.Navbar.inLiveChatTitle', { name }),
          message:
            text.length > 120
              ? text.slice(0, 117) + '…'
              : text || this.$t('Components.shared.Navbar.newLiveChatMessage'),
          dedupeKey: 'timeline-chat',
          route: { name: 'live-stream' }
        });
      }
      this.seenTimelineCount = currentCount;
    },
    // A promoted message targets "digital" and/or "webApp". Only notify
    // when it's meant for the platform this attendee is on — same rule as
    // LiveStream.vue (no targeting = show everywhere). Group targeting is
    // already applied server-side, so the store only holds items this user
    // is allowed to see.
    isPromotedForCurrentPlatform(item) {
      const options = item && item.PlatformDisplayOptions;
      if (!Array.isArray(options) || !options.length) return true;
      // eslint-disable-next-line no-unused-vars
      const _ = this.participationTick; // touch to force reactivity
      const platform = getApplicablePlatforms(this.$route)[0];
      return options.includes(platform);
    },
    checkPromotedItems() {
      const items = this.$store.state.Timeline?.promotedTimelineItems || [];
      items.forEach((item) => {
        if (!item || !item.TimelineID) return;
        if (this.seenPromotedIds.has(item.TimelineID)) return;
        this.seenPromotedIds.add(item.TimelineID);
        if (!this.isPromotedForCurrentPlatform(item)) return;
        const tmp = document.createElement('div');
        tmp.innerHTML = item.Message || '';
        const text = (tmp.textContent || tmp.innerText || '').trim();
        this.addNotification({
          type: 'promoted',
          title: this.$t('Components.shared.Navbar.newAnnouncement'),
          message:
            text || this.$t('Components.shared.Navbar.announcementFallback'),
          dedupeKey: `promoted-${item.TimelineID}`,
          route: { name: 'live-stream' }
        });
      });
    },
    startSessionTicker() {
      if (this.sessionTickInterval) return;
      // Check every 10 seconds for tighter detection
      this.sessionTickInterval = setInterval(
        () => this.scanSessionsForChanges(),
        10000
      );
      // First run: just mark sessions that have already started/ended
      // so we don't spam stale notifications on page load.
      this.scanSessionsForChanges(true);
    },
    stopSessionTicker() {
      if (this.sessionTickInterval) {
        clearInterval(this.sessionTickInterval);
        this.sessionTickInterval = null;
      }
    },
    // Set of session ids the user has booked (My Programs). Reminders
    // only fire for these so attendees aren't spammed about the whole
    // agenda.
    bookedSessionIds() {
      const items =
        (this.$store.state.BookSessions &&
          this.$store.state.BookSessions.items) ||
        [];
      return new Set(items.map((s) => s && s.id).filter(Boolean));
    },
    scanSessionsForChanges(initial = false) {
      const sessions = this.agendaSessions || [];
      if (!sessions.length) return;
      const now = Date.now();
      const booked = this.bookedSessionIds();
      // Reminder lead time — notify when a booked session is starting
      // within the next 10 minutes.
      const REMINDER_MS = 10 * 60 * 1000;
      sessions.forEach((s) => {
        if (!s || !s.id || !s.startDate || !s.endDate) return;
        const start = new Date(s.startDate).getTime();
        const end = new Date(s.endDate).getTime();

        // ── Schedule change — admin moved the session's time ──────────
        const prev = this.sessionSchedule[s.id];
        if (prev && (prev.start !== start || prev.end !== end)) {
          if (!initial) {
            this.addNotification({
              type: 'schedule-change',
              title: this.$t('Components.shared.Navbar.scheduleUpdated'),
              message: s.title
                ? this.$t('Components.shared.Navbar.sessionRescheduled', {
                    title: s.title,
                    time: this.formatScheduleTime(start)
                  })
                : this.$t('Components.shared.Navbar.agendaSessionRescheduled'),
              dedupeKey: `schedule-change-${s.id}-${start}`,
              route: { name: 'program' },
              meta: { sessionId: s.id }
            });
          }
          // A reschedule re-arms the start/reminder announcements.
          this.announcedSessions.started.delete(s.id);
          this.announcedSessions.reminded.delete(s.id);
        }
        this.sessionSchedule[s.id] = { start, end };

        // ── Reminder — booked session starting soon ───────────────────
        // Fires once per session (deduped via sessionStorage so it
        // doesn't repeat on reload). `initial` no longer suppresses it —
        // if a booked session is about to start when you load, you want
        // the heads-up immediately.
        if (
          booked.has(s.id) &&
          start > now &&
          start - now <= REMINDER_MS &&
          !this.announcedSessions.reminded.has(s.id)
        ) {
          this.announcedSessions.reminded.add(s.id);
          this.persistAnnouncedSessions();
          const mins = Math.max(1, Math.round((start - now) / 60000));
          this.addNotification({
            type: 'session-reminder',
            title: this.$t('Components.shared.Navbar.startingSoon'),
            message: s.title
              ? this.$t('Components.shared.Navbar.sessionStartsIn', {
                  title: s.title,
                  mins
                })
              : this.$t('Components.shared.Navbar.agendaSessionStartsIn', {
                  mins
                }),
            dedupeKey: `session-reminder-${s.id}`,
            route: { name: 'live-stream', query: { session: s.id } },
            meta: { sessionId: s.id }
          });
        }

        // ── Session is currently live ─────────────────────────────────
        // Notify once (even on first load) so the user immediately sees
        // what's live now; the persisted `started` set stops it repeating
        // across reloads.
        if (
          start <= now &&
          now < end &&
          !this.announcedSessions.started.has(s.id)
        ) {
          this.announcedSessions.started.add(s.id);
          this.persistAnnouncedSessions();
          const justStarted = now - start <= 2 * 60 * 1000;
          this.addNotification({
            type: 'session-start',
            title: justStarted
              ? this.$t('Components.shared.Navbar.sessionStarted')
              : this.$t('Components.shared.Navbar.sessionIsLive'),
            message: s.title
              ? this.$t('Components.shared.Navbar.sessionNowLive', {
                  title: s.title
                })
              : this.$t('Components.shared.Navbar.genericSessionLive'),
            dedupeKey: `session-start-${s.id}`,
            route: { name: 'live-stream', query: { session: s.id } },
            meta: { sessionId: s.id }
          });
        }
        // ── Session has ended ─────────────────────────────────────────
        if (end <= now && !this.announcedSessions.ended.has(s.id)) {
          // Only notify about an ending for a session we actually saw go
          // live — otherwise we'd spam every already-finished session on
          // first load.
          const sawStart = this.announcedSessions.started.has(s.id);
          this.announcedSessions.ended.add(s.id);
          this.persistAnnouncedSessions();
          if (sawStart && !initial) {
            this.addNotification({
              type: 'session-end',
              title: this.$t('Components.shared.Navbar.sessionEnded'),
              message: s.title
                ? this.$t('Components.shared.Navbar.sessionHasEnded', {
                    title: s.title
                  })
                : this.$t('Components.shared.Navbar.genericSessionEnded'),
              dedupeKey: `session-end-${s.id}`,
              meta: { sessionId: s.id }
            });
          }
        }
      });
    },
    // Announced session ids persist in sessionStorage so they survive a
    // page reload (same tab) — that's what stops "session is live"
    // notifications from re-firing every time the user refreshes.
    announcedSessionsKey() {
      return 'tappin_notif_sessions_v2';
    },
    loadAnnouncedSessions() {
      try {
        const raw = sessionStorage.getItem(this.announcedSessionsKey());
        const parsed = raw ? JSON.parse(raw) : {};
        this.announcedSessions = {
          started: new Set(parsed.started || []),
          ended: new Set(parsed.ended || []),
          reminded: new Set(parsed.reminded || [])
        };
      } catch (e) {
        /* keep the in-memory defaults */
      }
    },
    persistAnnouncedSessions() {
      try {
        sessionStorage.setItem(
          this.announcedSessionsKey(),
          JSON.stringify({
            started: Array.from(this.announcedSessions.started),
            ended: Array.from(this.announcedSessions.ended),
            reminded: Array.from(this.announcedSessions.reminded)
          })
        );
      } catch (e) {
        /* ignore */
      }
    },
    formatScheduleTime(ms) {
      try {
        return this.$moment(ms).format('ddd, MMM D HH:mm');
      } catch (e) {
        return this.$t('Components.shared.Navbar.aNewTime');
      }
    },

    goToHomePage() {
      if (this.$route.fullPath.includes('webapp')) {
        //webapp is redirected to home page
        return window.location.reload();
      }
      // Home = lobby when the attendee can see it, otherwise the program page
      // (matches the post-login flow). Every page navigates there.
      const home = this.isEventInfoVisible ? 'lobby' : 'program';
      // End any poll/quiz that is open: unmounting it stops it. The lobby keeps
      // it in the Info store; the live-stream page handles its own (Timeline) one below.
      this.$store.dispatch('Info/resetPromotedPage');
      if (this.$route.name !== 'live-stream') {
        this.$store.dispatch('Timeline/resetPromotedPage');
      }
      // Home always lands at the top of the page.
      const toTop = () =>
        this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'auto' }));
      if (this.$route.name === 'program' && home === 'program') {
        // Lobby off, already on the program page: show the featured (promoted messages) screen: close the agenda panel
        // if it is open and drop any open section.
        this.onRouteNavClick();
        this.$root.$emit('program:close-agenda');
        this.$root.$emit('live-stream:set-tab', null);
        this.agendaOpen = false;
        toTop();
        return;
      }
      if (this.$route.name === home) {
        // Already home: just close whichever section (info, sponsors, ...) is open.
        this.$root.$emit('live-stream:set-tab', null);
        toTop();
        return;
      }
      // Live stream with a poll/quiz/section open: close it like its back
      // button does (that is what returns the user home); otherwise navigate.
      if (this.$route.name === 'live-stream') {
        let closed = false;
        this.$root.$emit('live-stream:close-section', (open) => {
          closed = open;
        });
        if (closed) {
          toTop();
          return;
        }
      }
      return this.$router
        .push({ name: home })
        .then(toTop)
        .catch(() => {});
    },
    setNavItems() {
      // Runs from mounted(), often before the event details have loaded, so
      // lobbyElements can still be undefined. Throwing here aborted the rest of
      // mounted() and silently disabled the notification poller and listener.
      return (this.getEvent?.lobbyElements || []).map((a) => {
        if (
          (a.options.label === this.informationCaption &&
            this.isInformationShown) ||
          (a.options.label === this.sponsorCaption &&
            this.features.lobbySponsors) ||
          (a.options.label === this.agendaCaption && this.isAgendaShown) ||
          (a.options.label === this.speakersCaption && this.isSpeakersShown) ||
          (a.id.startsWith('sponsors') && this.isMultipleSponsorsShown)
        )
          this.lobbyElements.push(a);
      });
    },
    setAltImg(event) {
      event.target.src = this.getDefaultImage;
    },
    openChat() {
      // Desktop: open the LinkedIn-style docked "Messaging" inbox window
      // (bottom-right) so users can browse conversations and start new
      // ones without leaving the page. Mobile keeps the sidebar panel.
      const isMobile =
        typeof window !== 'undefined' && window.innerWidth <= 768;
      if (!isMobile) {
        this.$store.dispatch('Chat/openInbox');
        return;
      }
      this.sidebarCollapsed = false;
      this.setCurrentTab(this.chatTab);
      this.setCurrentChatID(null);
      this.setIsChatList(true);
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
      return true;
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
      return true; // So link click event won't be stopped
    },
    showChatModal(participant) {
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        ChatLobby,
        { participant },
        {
          clickToClose: false,
          adaptive: true,
          minWidth: 250,
          maxWidth: 1700,
          width: '75%',
          height: '95%'
        },
        { closed: () => document.body.classList.remove('overflow-hidden') }
      );
    },
    showProfileModal(profile) {
      document.body.classList.add('overflow-hidden');

      this.$modal.show(
        UserProfile,
        { profile },
        {
          clickToClose: true,
          adaptive: true,
          minWidth: 250,
          maxWidth: 1000,
          width: '90%',
          height: '80%'
        },
        {
          opened: () => this.$root.$emit('focusChild'),
          closed: () => document.body.classList.remove('overflow-hidden')
        }
      );
    },
    showTicketModal(profile) {
      document.body.classList.add('overflow-hidden');

      this.$modal.show(
        TicketModal,
        {},
        {
          clickToClose: false,
          adaptive: true,
          minWidth: 250,
          maxWidth: 1000,
          width: '50%',
          height: 'auto'
        },
        {
          opened: () => this.$root.$emit('focusChild'),
          closed: () => document.body.classList.remove('overflow-hidden')
        }
      );
    },
    showParticipantsList() {
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        LobbyParticipants,
        {},
        {
          clickToClose: true,
          adaptive: true,
          minWidth: 320,
          maxWidth: 640,
          width: '92%',
          height: 'auto'
        },
        {
          opened: () => this.$root.$emit('focusChild'),
          closed: () => document.body.classList.remove('overflow-hidden')
        }
      );
      this.closeMobileMenu();
    }
  },
  filters: {
    formatNavDate(value) {
      if (!value) return '';
      const d = new Date(value);
      return d.toLocaleDateString(intlLocale(), {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  },
  watch: {
    // Same-tab profile flips don't fire a `storage` event for the
    // tab that wrote the value, so we also bump the tick on route
    // navigation — when the user leaves the profile page after
    // toggling, the navbar refreshes the Event Info gating.
    $route() {
      this.participationTick += 1;
      // Close the mobile menu (and release the scroll lock) on navigation.
      this.mobileNavOpen = false;
      // Also close the in-place chat panel so it doesn't linger over a
      // page the user navigated to from the bottom bar.
      if (this.chatPanelOpen) this.closeChatPanel();
      // Reconcile the Posts highlight with the destination route: it stays
      // on only when we've actually landed on the live-stream Posts tab.
      if (this.$route.name !== 'program') this.agendaOpen = false;
      const onPage = ['live-stream', 'program'].includes(this.$route.name);
      if (!onPage) this._panelTab = null;
      // The address may lose ?tab=chat after arrival (redirects), so also
      // trust the section the page reports as open.
      this.postsActive =
        onPage &&
        (this.$route.query.tab === 'chat' || this._panelTab === 'chat');
    },
    // Lock body scroll while the mobile hamburger menu is open, so only the
    // menu itself scrolls and the page behind it stays put. Covers every
    // open/close path since they all funnel through this single flag.
    mobileNavOpen(open) {
      if (typeof document === 'undefined') return;
      // Don't release the scroll lock if the chat panel is still holding it.
      if (!open && this.chatPanelOpen) return;
      document.body.classList[open ? 'add' : 'remove']('overflow-hidden');
    },
    // Load (or reload) the notification log + preferences once we know
    // who the user is — the profile often resolves after mount.
    ownUserId(val) {
      if (val && this._notifHydratedFor !== val) {
        this.$store.dispatch('Notifications/hydrate', val);
        this._notifHydratedFor = val;
      }
    },
    // Robust safety net — fires on ANY change to the chat list,
    // regardless of whether the change came via pusher, polling,
    // or a direct store mutation. The mutation-subscription path
    // can miss events when pusher binds before the event store is
    // ready (returns a fake instance) or when payload shapes don't
    // line up. This deep watcher always sees fresh state.
    chatList: {
      handler(chats) {
        if (!Array.isArray(chats)) return;
        chats.forEach((chat) => {
          if (!chat || !chat.ChatID || !Array.isArray(chat.Messages)) return;
          const prev = this._chatMsgSeen[chat.ChatID];
          const current = chat.Messages.length;
          if (prev === undefined) {
            // First sighting of this chat — snapshot, do not notify
            // (avoids spamming on initial page load).
            this._chatMsgSeen[chat.ChatID] = current;
            return;
          }
          if (current <= prev) {
            this._chatMsgSeen[chat.ChatID] = current;
            return;
          }
          const newMsgs = chat.Messages.slice(prev).filter(
            (m) => m && m.UserID !== this.ownUserId
          );
          if (newMsgs.length) {
            const last = newMsgs[newMsgs.length - 1];
            const sender = (chat.Users || []).find(
              (u) => u && u.UserID === last.UserID
            );
            const senderName = sender
              ? `${sender.Firstname || sender.firstName || ''} ${
                  sender.Lastname || sender.lastName || ''
                }`.trim() || this.$t('Components.shared.Navbar.someone')
              : this.$t('Components.shared.Navbar.someone');
            const preview = (
              last.Message ||
              last.message ||
              this.$t('Components.shared.Navbar.sentYouAMessage')
            )
              .toString()
              .trim();
            this.addNotification({
              type: 'chat',
              title: this.$t('Components.shared.Navbar.sentYouAMessageTitle', {
                name: senderName
              }),
              message:
                preview.length > 120 ? preview.slice(0, 117) + '…' : preview,
              dedupeKey: `chat-${chat.ChatID}`,
              meta: { chatId: chat.ChatID, userId: last.UserID }
            });
          }
          this._chatMsgSeen[chat.ChatID] = current;
        });
      },
      deep: true
    },
    // Live-stream chat — same pattern.
    timelineFeed: {
      handler(items) {
        if (!Array.isArray(items)) return;
        const current = items.length;
        const prev = this._timelineSeenCount;
        if (prev === undefined) {
          this._timelineSeenCount = current;
          return;
        }
        if (current <= prev) {
          this._timelineSeenCount = current;
          return;
        }
        // Newest items are at the front (sorted Tid desc).
        const newItems = items.slice(0, current - prev).filter((i) => {
          if (!i || !i.Message) return false;
          const uid =
            i.UserID ||
            (i.Author && i.Author.UserID) ||
            (i.User && i.User.UserID);
          return uid && uid !== this.ownUserId;
        });
        if (newItems.length) {
          const last = newItems[0];
          const name =
            (last.Author && last.Author.Firstname) ||
            (last.User && last.User.Firstname) ||
            this.$t('Components.shared.Navbar.someone');
          const tmp = document.createElement('div');
          tmp.innerHTML = (last.Message || '').replace(
            /<!--tappin-session:[^>]*-->/g,
            ''
          );
          const text = (tmp.textContent || tmp.innerText || '').trim();
          this.addNotification({
            type: 'chat',
            title: this.$t('Components.shared.Navbar.inLiveChatTitle', {
              name
            }),
            message:
              text.length > 120
                ? text.slice(0, 117) + '…'
                : text ||
                  this.$t('Components.shared.Navbar.newLiveChatMessage'),
            dedupeKey: 'timeline-chat',
            route: { name: 'live-stream' }
          });
        }
        this._timelineSeenCount = current;
      },
      deep: true
    }
  },
  mounted() {
    this.setNavItems();
    // Load this user's persisted notification log + preferences so the
    // bell shows history from previous sessions and honours their
    // category opt-outs from the first notification onward. The profile
    // may not be loaded yet at mount — the `ownUserId` watcher below
    // re-runs this once it is.
    if (this.ownUserId) {
      this.$store.dispatch('Notifications/hydrate', this.ownUserId);
      this._notifHydratedFor = this.ownUserId;
    }
    // Non-reactive per-instance counters used by the chatList /
    // timelineFeed deep watchers above. Stored on `this` directly so
    // they don't trigger Vue reactivity (we read them, then write the
    // new count after diffing — no template binding needed).
    this._chatMsgSeen = {};
    this._timelineSeenCount = undefined;
    // Give data providers a tick to populate initial state before we
    // snapshot baselines, then start polling for changes every 5s.
    this.$nextTick(() => {
      this.startNotificationPoller();
    });
    // Restore which sessions were already announced this tab so reloads
    // don't replay "session is live" toasts.
    this.loadAnnouncedSessions();
    this.startSessionTicker();

    // Subscribe to the Vuex mutations that actually fire when chats
    // or timeline messages arrive (whether via pusher or poll). Each
    // case updates its own seen-counter so the poller's diff logic
    // doesn't double-fire for the same event.
    this._unsubChatStore = this.$store.subscribe((mutation) => {
      try {
        // ── Private chat: addChatMessage is the mutation committed
        //    both by the pusher addMessage action AND by direct sends.
        if (mutation.type === 'Chat/addChatMessage') {
          const payload = mutation.payload || {};
          const message = payload.item || payload;
          if (!message || !message.ChatID) return;
          // Skip my own sent messages
          if (this.ownUserId && message.UserID === this.ownUserId) return;
          const chat = (this.$store.state.Chat?.chats || []).find(
            (c) => c.ChatID === message.ChatID
          );
          // The backend also broadcasts every private message on the shared
          // event channel, so this fires for other people's chats too. Only
          // notify for chats this participant is a member of (the chat store
          // ignores the others the same way).
          if (!chat) return;
          const sender = (chat.Users || []).find(
            (u) => u && u.UserID === message.UserID
          );
          const senderName = sender
            ? `${sender.Firstname || sender.firstName || ''} ${
                sender.Lastname || sender.lastName || ''
              }`.trim() || this.$t('Components.shared.Navbar.someone')
            : this.$t('Components.shared.Navbar.someone');
          const preview = (
            message.Message ||
            message.message ||
            this.$t('Components.shared.Navbar.sentYouAMessage')
          )
            .toString()
            .trim();
          this.addNotification({
            type: 'chat',
            title: this.$t('Components.shared.Navbar.sentYouAMessageTitle', {
              name: senderName
            }),
            message:
              preview.length > 120 ? preview.slice(0, 117) + '…' : preview,
            dedupeKey: `chat-${message.ChatID}`,
            meta: { chatId: message.ChatID, userId: message.UserID }
          });
        }

        // ── Private chat: setChats mutation fires on loadChatData.
        //    Diff against the previous snapshot to catch messages that
        //    arrived while pusher was down.
        if (mutation.type === 'Chat/setChats') {
          const chats = mutation.payload || [];
          chats.forEach((chat) => {
            if (!chat || !chat.ChatID || !Array.isArray(chat.Messages)) return;
            const prev = this.seenChatMessages[chat.ChatID];
            const current = chat.Messages.length;
            if (prev === undefined) {
              // First time we've seen this chat — snapshot, don't notify.
              this.seenChatMessages[chat.ChatID] = current;
              return;
            }
            if (current <= prev) {
              this.seenChatMessages[chat.ChatID] = current;
              return;
            }
            const newMsgs = chat.Messages.slice(prev).filter(
              (m) => m && m.UserID !== this.ownUserId
            );
            if (newMsgs.length) {
              const last = newMsgs[newMsgs.length - 1];
              const sender = (chat.Users || []).find(
                (u) => u && u.UserID === last.UserID
              );
              const senderName = sender
                ? `${sender.Firstname || sender.firstName || ''} ${
                    sender.Lastname || sender.lastName || ''
                  }`.trim() || this.$t('Components.shared.Navbar.someone')
                : this.$t('Components.shared.Navbar.someone');
              const preview = (
                last.Message ||
                last.message ||
                this.$t('Components.shared.Navbar.sentYouAMessage')
              )
                .toString()
                .trim();
              this.addNotification({
                type: 'chat',
                title: this.$t(
                  'Components.shared.Navbar.sentYouAMessageTitle',
                  {
                    name: senderName
                  }
                ),
                message:
                  preview.length > 120 ? preview.slice(0, 117) + '…' : preview,
                dedupeKey: `chat-${chat.ChatID}`,
                meta: { chatId: chat.ChatID, userId: last.UserID }
              });
            }
            this.seenChatMessages[chat.ChatID] = current;
          });
        }

        // ── Live-stream chat: setTimelineItems fires on load +
        //    addTimelineItem. Diff to catch both pusher and poll.
        if (mutation.type === 'Timeline/setTimelineItems') {
          const payload = mutation.payload || {};
          const items = Array.isArray(payload) ? payload : payload.items || [];
          const prev = this.seenTimelineCount || 0;
          const current = items.length;
          if (!this._timelineBootstrapped) {
            this._timelineBootstrapped = true;
            this.seenTimelineCount = current;
            return;
          }
          if (current <= prev) {
            this.seenTimelineCount = current;
            return;
          }
          // New items are unshifted at the front (sorted by Tid desc)
          const newItems = items.slice(0, current - prev).filter((i) => {
            if (!i || !i.Message) return false;
            const uid =
              i.UserID ||
              (i.Author && i.Author.UserID) ||
              (i.User && i.User.UserID);
            return uid && uid !== this.ownUserId;
          });
          if (newItems.length) {
            const last = newItems[0];
            const name =
              (last.Author && last.Author.Firstname) ||
              (last.User && last.User.Firstname) ||
              this.$t('Components.shared.Navbar.someone');
            const tmp = document.createElement('div');
            tmp.innerHTML = (last.Message || '').replace(
              /<!--tappin-session:[^>]*-->/g,
              ''
            );
            const text = (tmp.textContent || tmp.innerText || '').trim();
            this.addNotification({
              type: 'chat',
              title: this.$t('Components.shared.Navbar.inLiveChatTitle', {
                name
              }),
              message:
                text.length > 120
                  ? text.slice(0, 117) + '…'
                  : text ||
                    this.$t('Components.shared.Navbar.newLiveChatMessage'),
              dedupeKey: 'timeline-chat',
              route: { name: 'live-stream' }
            });
          }
          this.seenTimelineCount = current;
        }

        // ── Admin announcements — promoted timeline items
        if (mutation.type === 'Timeline/setPromotedTimelineItems') {
          const payload = mutation.payload || {};
          const items = Array.isArray(payload) ? payload : payload.items || [];
          items.forEach((item) => {
            if (!item || !item.TimelineID) return;
            if (this.seenPromotedIds.has(item.TimelineID)) return;
            // Skip on first bootstrap — don't re-notify existing pins
            if (!this._promotedBootstrapped) return;
            this.seenPromotedIds.add(item.TimelineID);
            if (!this.isPromotedForCurrentPlatform(item)) return;
            const tmp = document.createElement('div');
            tmp.innerHTML = item.Message || '';
            const text = (tmp.textContent || tmp.innerText || '').trim();
            this.addNotification({
              type: 'promoted',
              title: this.$t('Components.shared.Navbar.newAnnouncement'),
              message:
                text ||
                this.$t('Components.shared.Navbar.announcementFallback'),
              dedupeKey: `promoted-${item.TimelineID}`,
              route: { name: 'live-stream' }
            });
          });
          if (!this._promotedBootstrapped) {
            // After the first load mark all current ids as seen
            items.forEach(
              (i) => i && i.TimelineID && this.seenPromotedIds.add(i.TimelineID)
            );
            this._promotedBootstrapped = true;
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Notifications] subscribe handler failed',
          mutation.type,
          e
        );
      }
    });

    // Request browser notification permission early so the prompt
    // appears while the user is actively on the page.
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
    // Close mobile menu on outside clicks
    document.addEventListener('click', this.onDocumentClickForMobileMenu);

    // Let other components (e.g. the live-stream "Open menu" button) open the
    // mobile hamburger menu via the shared $root event bus, so there is a
    // single menu on screen instead of a separate right-side drawer.
    this._openMobileNavFromBus = () => {
      // Defer to the next tick so the click that triggered this has finished
      // bubbling to the document-level close handler (which sees the menu
      // still closed and leaves it alone) before we actually open it —
      // otherwise the menu opens and is closed again by the same click.
      this.$nextTick(() => {
        this.mobileNavOpen = true;
      });
    };
    this.$root.$on('navbar:open-mobile-menu', this._openMobileNavFromBus);
    this._onAgendaState = (open) => {
      this.agendaOpen = !!open;
    };
    this.$root.$on('program:agenda-state', this._onAgendaState);
    // The page reports its open side-panel section: Posts open → highlight
    // Posts (not the underlying Program tab).
    this._onTabState = (key) => {
      this._panelTab = key || null;
      if (this.chatPanelOpen || this.agendaOpen) return;
      this.postsActive = key === 'chat';
    };
    this.$root.$on('live-stream:tab-state', this._onTabState);

    // Cross-tab participation changes — when another tab (or
    // profile-page in this tab) writes a new value, refresh the
    // tick so the Event Info link appears/disappears immediately.
    this._onParticipationStorage = (e) => {
      if (e && e.key && e.key.startsWith('tappin_participation_')) {
        this.participationTick += 1;
      }
    };
    window.addEventListener('storage', this._onParticipationStorage);
  },
  beforeDestroy() {
    this.stopSessionTicker();
    this.stopNotificationPoller();
    if (this._unsubChatStore) this._unsubChatStore();
    document.removeEventListener('click', this.onDocumentClickForMobileMenu);
    if (this._onParticipationStorage) {
      window.removeEventListener('storage', this._onParticipationStorage);
    }
    if (this._onAgendaState) {
      this.$root.$off('program:agenda-state', this._onAgendaState);
      this.$root.$off('live-stream:tab-state', this._onTabState);
    }
    if (this._openMobileNavFromBus) {
      this.$root.$off('navbar:open-mobile-menu', this._openMobileNavFromBus);
    }
    // Never leave the page scroll-locked if the navbar unmounts while the
    // mobile menu is open.
    document.body.classList.remove('overflow-hidden');
  }
};
</script>

<style lang="scss" scoped>
.navbar {
  background-color: #ffffff;
  font-family: 'Poppins', sans-serif;
  position: relative; /* Containing block for the mobile menu overlay */

  *:focus-visible {
    outline: 2px solid var(--color-primary, #047873);
    outline-offset: 2px;
  }
}

.skip-nav-link {
  position: absolute;
  left: -9999px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
  z-index: -1;

  &:focus {
    position: fixed;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    width: auto;
    height: auto;
    padding: 10px 20px;
    background: var(--color-primary, #047873);
    color: #ffffff;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    font-family: 'Poppins', sans-serif;
    text-decoration: none;
    z-index: 200;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
}

.brand-icon {
  // No fill — the brand-coloured chip lives only on the calendar
  // fallback (see the inline `<div v-else>` in the template). When
  // the admin uploads a real logo we want it on a transparent
  // wrapper so the logo's own background reads cleanly.
  background-color: transparent;
  border-radius: 10px;
  // Height is fixed so the navbar (h-16) doesn't change; width follows the
  // logo's aspect ratio, so wide logos get noticeably bigger.
  height: 56px;
  padding-left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 20px;

  &__img {
    height: 100%;
    width: auto;
    max-width: 150px;
  }
}

/* Nav link pill style */
.nav-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  text-decoration: none;
  transition: background-color 0.15s, color 0.15s;
  white-space: nowrap;
  font-family: 'Poppins', sans-serif;

  &:hover {
    background-color: #f3f4f6;
  }

  &--active {
    background-color: var(--color-primary, #047873);
    color: #ffffff !important;

    &:hover {
      background-color: var(--color-primary-hover, #036663);
    }
  }

  // Tablet: tighter padding
  @media (max-width: 900px) {
    padding: 8px 12px;
    font-size: 13px;
    gap: 5px;
  }
}

/* ── "Networking" AI accent icon ──────────────────────────────────────
   Networking is the AI-powered feature, so its icon uses the sparkle
   glyph plus a brand-tinted gradient fill (with a slow shimmer) to read
   clearly as "AI" and stand out from the plain gray nav icons. The
   gradient is built from the event's own brand vars, so it stays on
   theme per event. Scoped to this one class — nothing else is touched. */
.nav-ai-icon {
  background: linear-gradient(
    120deg,
    var(--color-primary, #047873) 0%,
    var(--color-secondary, #22aaa2) 45%,
    var(--color-primary, #047873) 90%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  // Fallback for the rare browser without background-clip: text.
  color: var(--color-primary, #047873);
}
@media (prefers-reduced-motion: no-preference) {
  .nav-ai-icon {
    animation: nav-ai-shimmer 3s ease-in-out infinite;
  }
}
@keyframes nav-ai-shimmer {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}
// On the solid brand pill (active state) keep the icon white so it stays
// legible — override the gradient fill and the shimmer.
.nav-link--active .nav-ai-icon,
.bottom-nav__item--active .nav-ai-icon {
  background: none;
  -webkit-text-fill-color: #ffffff;
  color: #ffffff;
  animation: none;
}

/* ── Desktop / Mobile visibility ──────────────────────────────────── */
.nav-desktop {
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 640px) {
    display: none;
  }
}

.nav-support-btn {
  padding: 8px;
  border-radius: 50%;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f3f4f6;
  }

  @media (max-width: 640px) {
    display: none;
  }
}

.mobile-hamburger {
  display: none;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #f3f4f6;
  }

  &--open {
    background: var(--color-primary, #047873);
    border-color: var(--color-primary, #047873);
    color: #ffffff;

    &:hover {
      background: var(--color-primary-hover, #036663);
    }
  }

  @media (max-width: 640px) {
    display: flex;
  }
}

/* ── Mobile menu overlay ─────────────────────────────────────────── */
// ── Mobile bottom-nav pill ───────────────────────────────────────────
// Floats at the bottom of the viewport on phones. White pill with
// three primary destinations (Event Info / Program / Profile). The
// active item gets a brand-tinted inner chip; inactive items stay
// muted-gray icons. Width hugs near the screen edges; height is
// kept compact so it doesn't dominate the bottom of the viewport.
.bottom-nav {
  display: none;
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 16px;
  z-index: 2147483640;
  align-items: center;
  justify-content: space-around;
  gap: 4px;
  padding: 5px 6px;
  border-radius: 9999px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06);
  font-family: 'Poppins', sans-serif;

  @media (max-width: 640px) {
    display: flex;
  }

  &__item {
    // Content-sized so the active item can grow to fit its label (an
    // "expanding pill") instead of every item sharing an equal, cramped
    // slot — which clipped the active label once Posts/Chat were added
    // and made tapping between sections feel jumpy.
    flex: 0 1 auto;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    height: 36px;
    padding: 0 12px;
    white-space: nowrap;
    // Kill the mobile tap-highlight flash and the 300ms tap delay so
    // taps register immediately.
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    // Reset button styling so the <button> entries (e.g. Posts) match the
    // <router-link> ones exactly.
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
    border-radius: 9999px;
    color: #6b7280;
    font-size: 12.5px;
    font-weight: 600;
    text-decoration: none;
    transition: background 0.22s ease, color 0.22s ease;

    i {
      font-size: 15px;
    }

    // Icon + unread badge wrapper (Chat). Keeps the badge pinned to the
    // icon's top-right corner without shifting the row.
    .bottom-nav__icon-wrap {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .bottom-nav__badge {
      position: absolute;
      top: -6px;
      left: 10px;
      min-width: 15px;
      height: 15px;
      padding: 0 4px;
      border-radius: 9999px;
      background: #ef4444;
      color: #ffffff;
      font-size: 9px;
      font-weight: 700;
      line-height: 15px;
      text-align: center;
      box-shadow: 0 0 0 2px #ffffff;
    }

    &:hover,
    &:focus-visible {
      color: #111827;
      outline: none;
    }

    // The label stays in the DOM but collapsed to zero width when the
    // item is inactive, then eases open on the active item — a smooth
    // expand rather than an abrupt show/hide. `min-width: 0` + ellipsis
    // let it shrink instead of overflowing the pill on narrow screens.
    .bottom-nav__label {
      max-width: 0;
      margin-left: 0;
      opacity: 0;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      transition: max-width 0.28s ease, opacity 0.2s ease,
        margin-left 0.28s ease;
    }

    &--active {
      background: var(--color-primary, #047873);
      color: #ffffff;

      i {
        color: #ffffff;
      }

      .bottom-nav__label {
        max-width: 120px;
        margin-left: 6px;
        opacity: 1;
      }

      &:hover,
      &:focus-visible {
        background: var(--color-primary, #047873);
        color: #ffffff;
      }
    }

    // Respect reduced-motion — collapse the eased expand to an instant
    // swap for users who opt out of animation.
    @media (prefers-reduced-motion: reduce) {
      transition: none;

      .bottom-nav__label {
        transition: none;
      }
    }
  }
}

/* ── Mobile in-place Chat panel ───────────────────────────────────────
   Full-screen chat surface opened from the bottom-nav Chat item. Sits
   ABOVE page content but BELOW the bottom-nav pill (which has a far
   higher z-index), and reserves clearance at the bottom so the chat's
   own input isn't hidden behind the floating pill. Mobile only. */
.mobile-chat-panel {
  position: fixed;
  inset: 0;
  // Clear the floating bottom-nav pill (≈46px tall, 16px from the edge).
  padding-bottom: 78px;
  background: var(--background-color, #ffffff);
  z-index: 2147483630;
  overflow: hidden;

  // PrivateChatModal fills its host.
  ::v-deep .pcm {
    width: 100%;
    height: 100%;
    border-radius: 0;
    box-shadow: none;
  }

  @media (min-width: 641px) {
    display: none;
  }
}

.mobile-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
  z-index: 50;
  padding: 10px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: 'Poppins', sans-serif;
  // Keep the menu within the viewport and let ITS OWN content scroll, rather
  // than the whole page, when the item list is taller than the screen.
  // Use dvh (dynamic viewport height) so the cap tracks the ACTUAL visible
  // area on mobile — plain `vh` excludes the address bar, leaving the box
  // tall enough that the content never overflows and the bottom items sit
  // below the fold with no way to scroll to them. vh is the fallback for
  // older browsers that don't support dvh.
  max-height: calc(100vh - 60px);
  max-height: calc(100dvh - 60px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 14px;
    border: none;
    background: none;
    border-radius: 12px;
    cursor: pointer;
    font-family: inherit;
    text-decoration: none;
    color: #374151;
    font-size: 14px;
    font-weight: 600;
    transition: background 0.12s, color 0.12s;

    &:hover {
      background: #f3f4f6;
    }

    &--active {
      background: var(--color-primary, #047873);
      color: #ffffff;

      .mobile-menu__icon {
        background: rgba(255, 255, 255, 0.2) !important;
        color: #ffffff !important;
      }

      &:hover {
        background: var(--color-primary-hover, #036663);
      }
    }
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    flex-shrink: 0;

    &--teal {
      background: #e6f5f4;
      color: var(--color-primary, #047873);
    }
    &--purple {
      background: #f3e8ff;
      color: #7e22ce;
    }
    &--orange {
      background: #fef3c7;
      color: #d97706;
    }
    &--green {
      background: #dcfce7;
      color: #15803d;
    }
  }

  &__label {
    flex: 1;
    text-align: left;
  }

  // Uppercase section header used between groups (e.g. the
  // live-stream tabs sit above a divider, secondary actions below).
  &__section-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #6b7280;
    padding: 4px 4px 8px;
  }

  &__divider {
    height: 1px;
    background: #f1f2f4;
    margin: 8px -4px;
  }
}

.mobile-menu-slide-enter-active,
.mobile-menu-slide-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.mobile-menu-slide-enter,
.mobile-menu-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ── Profile dropdown ──────────────────────────────────────────────── */
.profile-dropdown-wrap {
  position: relative;

  &__chevron {
    font-size: 9px;
    margin-left: 2px;
    transition: transform 0.15s;
    opacity: 0.6;

    &--open {
      transform: rotate(180deg);
    }
  }

  /* On mobile, hide chevron and label — icon only */
  @media (max-width: 640px) {
    &__chevron {
      display: none;
    }
  }
}

.profile-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 220px;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  z-index: 60;
  overflow: hidden;
  padding: 6px;
  font-family: 'Poppins', sans-serif;

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 10px 12px;
    border: none;
    background: none;
    border-radius: 10px;
    cursor: pointer;
    font-family: inherit;
    text-decoration: none;
    color: #374151;
    font-size: 13px;
    font-weight: 600;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: #f3f4f6;
      color: #111827;
    }
  }

  &__icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;

    &--teal {
      background: #e6f5f4;
      color: var(--color-primary, #047873);
    }
    &--purple {
      background: #f3e8ff;
      color: #7e22ce;
    }
    &--orange {
      background: #fef3c7;
      color: #d97706;
    }
  }

  &__label {
    flex: 1;
    text-align: left;
  }
}

@media (max-width: 480px) {
  .profile-dropdown {
    right: -24px;
    width: 200px;
  }
}

/* Brand text — truncate name and hide date on small screens */
.brand-wrap {
  min-width: 0;
}

// Event name under the separator: bold, brand-coloured, with an accent bar
// so it stands out from the body text below it.
.brand-event-name {
  // No bottom padding: every page already adds its own top gap below the
  // navbar, so the name's top padding is sized to match it.
  padding-top: 16px;
  padding-bottom: 0;
  // Same background as the page content (dark-mode aware), not the navbar's.
  background-color: var(--background-color, #ffffff);

  &__text {
    font-size: 22px;
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: -0.01em;
    color: var(--color-text, #000000);
    overflow-wrap: anywhere;

    &--inline {
      font-size: 18px;
    }
  }
}

::v-deep .brand-icon {
  @media (max-width: 480px) {
    height: 48px !important;
    font-size: 16px;
  }
  @media (max-width: 480px) {
    .brand-icon__img {
      max-width: 120px;
    }
  }
}

/* Notification badge */
.notification-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  background: #ef4444;
  color: #ffffff;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* ── Notifications bell + dropdown ─────────────────────────────────── */
.notif-wrap {
  position: relative;
}

.theme-toggle-btn {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-family: 'Poppins', sans-serif;
  transition: background 0.15s, border-color 0.15s, color 0.15s, transform 0.2s;

  &:hover,
  &:focus-visible {
    background: #f9fafb;
    border-color: var(--color-primary, #047873);
    color: var(--color-primary, #047873);
    outline: none;
  }

  &--dark {
    background: rgba(251, 191, 36, 0.12);
    border-color: rgba(251, 191, 36, 0.4);
    color: #f59e0b;

    &:hover,
    &:focus-visible {
      background: rgba(251, 191, 36, 0.2);
      border-color: #f59e0b;
      color: #d97706;
    }
  }

  i {
    transition: transform 0.25s ease;
  }
  &:active i {
    transform: rotate(-20deg) scale(0.92);
  }
}

.notif-btn {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-family: 'Poppins', sans-serif;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover {
    background: #f9fafb;
    border-color: var(--color-primary, #047873);
    color: var(--color-primary, #047873);
  }

  &--open {
    background: #f9fafb;
    border-color: var(--color-primary, #047873);
    color: var(--color-primary, #047873);
  }
}

.notif-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9999px;
  background: #ef4444;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
  line-height: 1;
}

.notif-panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 360px;
  max-width: calc(100vw - 24px);
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  z-index: 60;
  overflow: hidden;
  font-family: 'Poppins', sans-serif;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid #f3f4f6;
    background: #f9fafb;
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  &__action {
    background: none;
    border: none;
    color: var(--color-primary, #047873);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 6px;
    font-family: inherit;

    &:hover {
      background: #e6f5f4;
    }
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__icon-action {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: none;
    background: none;
    color: #6b7280;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;

    &:hover {
      background: #e6f5f4;
      color: var(--color-primary, #047873);
    }
    &--active {
      color: var(--color-primary, #047873);
    }
  }

  &__list {
    max-height: 380px;
    overflow-y: auto;
  }

  &__empty {
    padding: 36px 24px;
    text-align: center;
    color: #6b7280;

    i {
      font-size: 32px;
      color: #d1d5db;
      margin-bottom: 10px;
    }

    p {
      font-size: 14px;
      font-weight: 600;
      color: #374151;
      margin: 0 0 4px;
    }

    span {
      font-size: 12px;
      color: #6b7280;
    }
  }

  &__footer {
    padding: 10px 14px;
    border-top: 1px solid #f3f4f6;
    background: #f9fafb;
    display: flex;
    justify-content: center;
  }

  &__clear {
    background: none;
    border: none;
    color: #6b7280;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 6px 12px;
    border-radius: 6px;
    font-family: inherit;

    &:hover {
      color: #ef4444;
      background: #fee2e2;
    }
  }
}

// ── Notification settings view ──────────────────────────────────────
.notif-settings {
  max-height: 380px;
  overflow-y: auto;
  padding: 6px 8px 10px;

  &__hint {
    font-size: 12px;
    color: #6b7280;
    margin: 8px 10px 6px;
  }
}

.notif-setting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 10px;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }

  &__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
  }

  &__desc {
    font-size: 11.5px;
    color: #6b7280;
    margin-top: 2px;
  }
}

// iOS-style toggle switch
.notif-toggle {
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;

  &__input {
    position: absolute;
    opacity: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    cursor: pointer;
  }

  &__track {
    width: 38px;
    height: 22px;
    border-radius: 9999px;
    background: #d1d5db;
    transition: background 0.18s;
    display: inline-flex;
    align-items: center;
    padding: 2px;
  }

  &__thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    transition: transform 0.18s;
  }

  &--on .notif-toggle__track {
    background: var(--color-primary, #047873);
  }
  &--on .notif-toggle__thumb {
    transform: translateX(16px);
  }
}

.notif-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 14px 18px;
  background: #ffffff;
  border: none;
  border-bottom: 1px solid #f3f4f6;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  position: relative;
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }

  &--unread {
    background: #f0fbf9;

    &:hover {
      background: #e6f5f4;
    }
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;

    &--chat {
      background: #dbeafe;
      color: #1d4ed8;
    }
    &--session-start {
      background: #dcfce7;
      color: #15803d;
    }
    &--session-end {
      background: #fef3c7;
      color: #d97706;
    }
    &--poll {
      background: #e6f5f4;
      color: var(--color-primary, #047873);
    }
    &--info {
      background: #f3f4f6;
      color: #6b7280;
    }
    &--promoted {
      background: #f3e8ff;
      color: #7e22ce;
    }
    &--sponsor {
      background: #fee2e2;
      color: #dc2626;
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    line-height: 1.35;
    margin-bottom: 3px;
  }

  &__message {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.45;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &__time {
    font-size: 10px;
    color: #6b7280;
    margin-top: 4px;
    font-weight: 500;
  }

  &__dot {
    position: absolute;
    top: 18px;
    right: 14px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-primary, #047873);
    flex-shrink: 0;
  }
}

.notif-fade-enter-active,
.notif-fade-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}
.notif-fade-enter,
.notif-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (max-width: 640px) {
  // On narrow viewports, anchor the notifications panel to the viewport
  // itself rather than to the bell button. This prevents the left edge
  // from being pushed off-screen when the bell sits near the right side
  // of the navbar and the panel is wider than the bell's containing
  // area can accommodate.
  .notif-panel {
    position: fixed;
    top: 72px; // below the 64 px navbar + 8 px breathing room
    left: 12px;
    right: 12px;
    width: auto;
    max-width: none;
    max-height: calc(100vh - 88px);
    overflow-y: auto;
  }
}

/* Mobile sidebar overlay (Stage route) */
@media only screen and (max-width: 1024px) {
  .navbar-sidebar {
    height: 100vh;
    font-size: medium;
    z-index: 29;
    display: flex !important;
    flex-direction: column;
    position: absolute;
    top: 0;
    right: 0;
    margin: 0;
  }
  @supports (height: 100dvh) {
    .navbar-sidebar {
      height: 100dvh;
    }
  }
}

/* Accessibility: skip link */
a.skip-main {
  left: -999px;
  position: absolute;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
  z-index: -999;
}
a.skip-main:focus {
  left: auto;
  top: auto;
  width: 30%;
  height: auto;
  overflow: auto;
  margin: 5% 35% 0 35%;
  padding: 5px;
  font-size: 20px;
  background: white;
  text-align: center;
  color: inherit;
  z-index: 999;
}
</style>
